import { TILE_W, TILE_H } from './constants';

/**
 * Convert tile coordinates to screen (pixel) coordinates
 * @param {number} tx - Tile X coordinate
 * @param {number} ty - Tile Y coordinate
 * @returns {{ x: number, y: number }} Screen coordinates
 */
export function isoToScreen(tx, ty) {
  return {
    x: (tx - ty) * (TILE_W / 2),
    y: (tx + ty) * (TILE_H / 2),
  };
}

/**
 * Convert screen (pixel) coordinates to tile coordinates
 * @param {number} x - Screen X coordinate
 * @param {number} y - Screen Y coordinate
 * @returns {{ tx: number, ty: number }} Tile coordinates (floating point)
 */
export function screenToIso(x, y) {
  const tx = (y / (TILE_H / 2) + x / (TILE_W / 2)) / 2;
  const ty = (y / (TILE_H / 2) - x / (TILE_W / 2)) / 2;
  return { tx, ty };
}

/**
 * Snap tile coordinates to grid
 * @param {number} tx - Tile X coordinate
 * @param {number} ty - Tile Y coordinate
 * @returns {{ tx: number, ty: number }} Snapped tile coordinates
 */
export function snapToGrid(tx, ty) {
  return {
    tx: Math.round(tx),
    ty: Math.round(ty),
  };
}

/**
 * Calculate depth sort key for an entity
 * Higher values render on top (later in the draw order)
 * @param {number} tx - Tile X coordinate
 * @param {number} ty - Tile Y coordinate
 * @param {number} sortOffset - Optional offset for tall objects
 * @returns {number} Sort key (zIndex)
 */
export function calculateDepthSort(tx, ty, sortOffset = 0) {
  // Base sort: (tx + ty) gives diagonal bands
  // Tie-breakers: ty, tx for consistent ordering
  return (tx + ty) * 1000 + ty * 10 + tx + sortOffset;
}

/**
 * Get the bounding box corners for a footprint at a tile position
 * @param {number} tx - Tile X coordinate
 * @param {number} ty - Tile Y coordinate
 * @param {{ w: number, h: number }} footprint - Footprint size in tiles
 * @returns {{ nw: {x,y}, ne: {x,y}, se: {x,y}, sw: {x,y} }} Corner screen coordinates
 */
export function getFootprintCorners(tx, ty, footprint) {
  const { w, h } = footprint;
  
  return {
    // Northwest (top)
    nw: isoToScreen(tx, ty),
    // Northeast (right)
    ne: isoToScreen(tx + w, ty),
    // Southeast (bottom)
    se: isoToScreen(tx + w, ty + h),
    // Southwest (left)
    sw: isoToScreen(tx, ty + h),
  };
}

/**
 * Check if two entities overlap based on their footprints
 * @param {{ tx: number, ty: number, footprint: { w: number, h: number } }} a 
 * @param {{ tx: number, ty: number, footprint: { w: number, h: number } }} b 
 * @returns {boolean}
 */
export function footprintsOverlap(a, b) {
  const aLeft = a.tx;
  const aRight = a.tx + a.footprint.w;
  const aTop = a.ty;
  const aBottom = a.ty + a.footprint.h;

  const bLeft = b.tx;
  const bRight = b.tx + b.footprint.w;
  const bTop = b.ty;
  const bBottom = b.ty + b.footprint.h;

  return !(aRight <= bLeft || bRight <= aLeft || aBottom <= bTop || bBottom <= aTop);
}
